// 114/115/116/117 の表示専用メタ行 helper をまとめる service です。
export function createFukushizaiDisplayMetaService(deps) {
    const {
        PANEL_TYPE,
        KOMONOFUDA_SIZE_RATE_FIELD_IDS,
        isEmpty,
        DicVal,
        canonicalPanelType,
        brandNameSourceColumnId,
        otherNameSourceColumnId,
        otherFukuzaiSourceColumnId,
        readFukushizaiMasterCodeNameFromValue
    } = deps;

    // 表示行へ base row id / source column id のメタ情報を付与します。
    function setFukushizaiDisplayMeta(row, baseRowId, sourceColumnId) {
        if (isEmpty(row) || typeof row !== "object") return row;
        if (!isEmpty(baseRowId)) row.__fukushizaiBaseRowId = String(baseRowId ?? "").trim();
        if (!isEmpty(sourceColumnId)) row.__fukushizaiSourceColumnId = String(sourceColumnId ?? "").trim();
        return row;
    }

    // 表示行の base row id を返します。
    function readFukushizaiDisplayBaseRowId(row) {
        if (isEmpty(row)) return "";
        return readFukushizaiDisplayExplicitBaseRowId(row)
            || String(DicVal(row, "rowid", "") ?? "").trim();
    }

    // 表示行に明示的に付与された base row id を返します。
    function readFukushizaiDisplayExplicitBaseRowId(row) {
        if (isEmpty(row)) return "";
        return String(DicVal(row, "__fukushizaiBaseRowId", "") ?? "").trim()
            || String(DicVal(row, "__komonofudaBaseRowId", "") ?? "").trim()
            || String(DicVal(row, "__brandNameBaseRowId", "") ?? "").trim()
            || String(DicVal(row, "__otherNameBaseRowId", "") ?? "").trim()
            || String(DicVal(row, "__otherFukuzaiBaseRowId", "") ?? "").trim();
    }

    // 表示行に紐づく source column id を返します。
    function readFukushizaiDisplaySourceColumnId(row) {
        if (isEmpty(row)) return "";
        return String(DicVal(row, "__fukushizaiSourceColumnId", "") ?? "").trim()
            || String(DicVal(row, "__komonofudaSourceColumnId", "") ?? "").trim()
            || String(DicVal(row, "__brandNameSourceColumnId", "") ?? "").trim()
            || String(DicVal(row, "__otherNameSourceColumnId", "") ?? "").trim()
            || String(DicVal(row, "__otherFukuzaiSourceColumnId", "") ?? "").trim();
    }

    // code:name 形式から code を取り出します。
    function extractMasterCodeFromDisplayValue(value) {
        const text = String(value ?? "").trim();
        if (text === "") return "";
        return text.split(":")[0].trim();
    }

    // code:name 形式から name を取り出します。
    function extractMasterNameFromDisplayValue(value) {
        const text = String(value ?? "").trim();
        if (text === "" || !text.includes(":")) return "";
        return text.substring(text.indexOf(":") + 1).trim();
    }

    // panel type ごとの表示列 id を返します。
    function getFukushizaiDisplayFieldIdByPanelType(type) {
        const canonicalType = canonicalPanelType(type);
        if (canonicalType === PANEL_TYPE.BRAND_NAME) return "BRANDNAME_CD_DMY";
        if (canonicalType === PANEL_TYPE.OTHER_NAME) return "SONOTANAME_CD_DMY";
        if (canonicalType === PANEL_TYPE.OTHER_FUKUZAI) return "SONOTAFUZOKU_CD_DMY";
        if (canonicalType === PANEL_TYPE.KOMONOFUDA) return "KOMONOFUDA_NAIYO_DMY";
        return "";
    }

    // 表示行 index を返します。
    function getFukushizaiDisplayRowIndex(row, fallbackIndex = -1) {
        const fallback = Number(fallbackIndex);
        if (Number.isFinite(fallback) && fallback >= 0) return fallback;
        const komonoIndex = Number(DicVal(row, "__komonofudaIndex", 0));
        if (Number.isFinite(komonoIndex) && komonoIndex > 0) return komonoIndex - 1;
        const rowid = Number(String(DicVal(row, "rowid", "") ?? "").trim());
        if (Number.isFinite(rowid) && rowid >= 0) return rowid;
        return -1;
    }

    // panel type と表示行 index から source column id を返します。
    function getFukushizaiDisplaySourceColumnIdByIndex(type, rowIndex) {
        const canonicalType = canonicalPanelType(type);
        const index = Number(rowIndex);
        if (!Number.isFinite(index) || index < 0) return "";
        if (canonicalType === PANEL_TYPE.KOMONOFUDA) return KOMONOFUDA_SIZE_RATE_FIELD_IDS[index] || "";
        if (canonicalType === PANEL_TYPE.BRAND_NAME) return brandNameSourceColumnId(index + 1);
        if (canonicalType === PANEL_TYPE.OTHER_NAME) return otherNameSourceColumnId(index + 1);
        if (canonicalType === PANEL_TYPE.OTHER_FUKUZAI) return otherFukuzaiSourceColumnId(index + 1);
        return "";
    }

    // 行データから source column id と row index を解決します。
    function resolveFukushizaiDisplaySourceColumnId(type, row, rowIndex = -1) {
        const explicit = readFukushizaiDisplaySourceColumnId(row)
            || String(DicVal(row, "sourceColumnId", "") ?? "").trim();
        if (explicit !== "") {
            return {
                sourceColumnId: explicit,
                rowIndex: getFukushizaiDisplayRowIndex(row, rowIndex),
                fallbackSourceColumnIdUsed: false
            };
        }
        const resolvedIndex = getFukushizaiDisplayRowIndex(row, rowIndex);
        return {
            sourceColumnId: getFukushizaiDisplaySourceColumnIdByIndex(type, resolvedIndex),
            rowIndex: resolvedIndex,
            fallbackSourceColumnIdUsed: true
        };
    }

    // 表示行から保存用 code を抽出します。
    function readFukushizaiDisplaySaveCode(row, displayFieldId, sourceColumnId) {
        const keys = [
            displayFieldId,
            sourceColumnId,
            `${sourceColumnId}_cd`,
            `${sourceColumnId}_CD`,
            `${displayFieldId}_cd`,
            `${displayFieldId}_CD`
        ].filter((key) => !isEmpty(key));
        for (let i = 0; i < keys.length; i++) {
            const value = DicVal(row, keys[i], undefined);
            if (value === undefined || value === null) continue;
            if (typeof value === "object") {
                const codeName = readFukushizaiMasterCodeNameFromValue(sourceColumnId, value);
                if (codeName.code !== "") return codeName.code;
                continue;
            }
            const code = extractMasterCodeFromDisplayValue(value);
            if (code !== "") return code;
        }
        return "";
    }

    // 候補配列から最初の非空文字列を返します。
    function readFirstFukushizaiDisplayText(row, keys) {
        const list = Array.isArray(keys) ? keys : [];
        for (let i = 0; i < list.length; i++) {
            const value = String(DicVal(row, list[i], "") ?? "").trim();
            if (value !== "") return value;
        }
        return "";
    }

    // 表示行から code / name / displayValue / displayFieldId を抽出します。
    function extractFukushizaiDisplayCodeName(panelInfo, row) {
        const type = canonicalPanelType(DicVal(panelInfo, "type", ""));
        const displayFieldId = getFukushizaiDisplayFieldIdByPanelType(type);
        const displayValue = String(DicVal(row, displayFieldId, "") ?? "").trim();
        const code = extractMasterCodeFromDisplayValue(displayValue);
        let name = "";
        if (type === PANEL_TYPE.BRAND_NAME) {
            name = readFirstFukushizaiDisplayText(row, [
                "BRANDNAME_NM_DMY",
                "BRANDNAME_CD_DMY_nm",
                "BRANDNAME_CD_DMY_NM"
            ]);
        } else if (type === PANEL_TYPE.OTHER_NAME) {
            name = readFirstFukushizaiDisplayText(row, [
                "SONOTANAME_NM_DMY",
                "SONOTANAME_CD_DMY_nm",
                "SONOTANAME_CD_DMY_NM"
            ]);
        } else if (type === PANEL_TYPE.OTHER_FUKUZAI) {
            name = readFirstFukushizaiDisplayText(row, [
                "SONOTAFUZOKU_NM_DMY",
                "SONOTAFUZOKU_CD_DMY_nm",
                "SONOTAFUZOKU_CD_DMY_NM"
            ]);
        }
        if (name === "") {
            name = extractMasterNameFromDisplayValue(displayValue);
        }
        return { code, name, displayValue, displayFieldId };
    }

    return {
        setFukushizaiDisplayMeta,
        readFukushizaiDisplayBaseRowId,
        readFukushizaiDisplayExplicitBaseRowId,
        readFukushizaiDisplaySourceColumnId,
        extractMasterCodeFromDisplayValue,
        extractMasterNameFromDisplayValue,
        getFukushizaiDisplayFieldIdByPanelType,
        getFukushizaiDisplayRowIndex,
        getFukushizaiDisplaySourceColumnIdByIndex,
        resolveFukushizaiDisplaySourceColumnId,
        readFukushizaiDisplaySaveCode,
        readFirstFukushizaiDisplayText,
        extractFukushizaiDisplayCodeName
    };
}
